import { createWithEqualityFn } from 'zustand/traditional';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

import type { PublicGame } from '~/domains/contest/domains/services/interfaces/propsService/api/game';

import type {
  PublicMarketGroup,
  PublicMarketGroupGamelineSection,
  MarketFilter,
  MarketSubCategory,
  GetPredictionMarketsDiscoverParams,
  PublicPredictionMarket,
} from '../services/interfaces/market.types';

import type { NormalizedMarketGroup } from './normalizedMarkets.normalization';
import {
  extractBettingLinesForGroup,
  mergeAndNormalizeMarketGroups,
} from './normalizedMarkets.normalization';

export type SubCategory = {
  id: MarketSubCategory;
  name: string;
  iconUrl: string;
  /** Inactive leagues stay in the list but cannot be opened. */
  active: boolean;
  /** Drives the league screen tabs; an empty list means the league has no markets to show. */
  filters: MarketFilter[];
};

type NormalizedMarketsState = {
  marketGroupsById: Map<string, PublicMarketGroup>;

  normalizedMarketGroupsById: Map<string, NormalizedMarketGroup>;

  marketIdToGroupId: Map<string, string>;

  marketTimestamps: Map<string, number>;

  activeMarketGroupId: string | null;
  discoveryParams: GetPredictionMarketsDiscoverParams | null;
  discoverySections: PublicMarketGroupGamelineSection[];
  subCategories: SubCategory[];

  isDiscoveryLoading: boolean;
  isSubCategoriesLoading: boolean;
  isDetailLoading: boolean;
  isBrowseLoading: boolean;
  discoveryError: Error | null;
  subCategoriesError: Error | null;
  detailError: Error | null;
  browseError: Error | null;

  setDiscoveryData: (
    sections: PublicMarketGroupGamelineSection[],
    eventsById: Map<string, PublicGame>
  ) => void;
  setSubCategories: (subCategories: SubCategory[]) => void;
  appendBrowseData: (
    marketGroups: PublicMarketGroup[],
    eventsById: Map<string, PublicGame>
  ) => void;
  setDetailData: (
    marketGroup: PublicMarketGroup & { event_start: number },
    eventsById: Map<string, PublicGame>
  ) => void;
  updateMarket: (marketId: string, marketData: PublicPredictionMarket, timestamp?: number) => void;
  updateMarketsBatch: (
    updates: Array<{ marketId: string; market: PublicPredictionMarket; timestamp?: number }>
  ) => void;
  setActiveMarketGroupId: (id: string | null) => void;
  setDiscoveryParams: (params: GetPredictionMarketsDiscoverParams | null) => void;
  setDiscoveryLoading: (loading: boolean) => void;
  setSubCategoriesLoading: (loading: boolean) => void;
  setDetailLoading: (loading: boolean) => void;
  setBrowseLoading: (loading: boolean) => void;
  setDiscoveryError: (error: Error | null) => void;
  setSubCategoriesError: (error: Error | null) => void;
  setDetailError: (error: Error | null) => void;
  setBrowseError: (error: Error | null) => void;
  reset: () => void;
};

type NormalizedMarketsStateData = Omit<
  NormalizedMarketsState,
  | 'setDiscoveryData'
  | 'setSubCategories'
  | 'appendBrowseData'
  | 'setDetailData'
  | 'updateMarket'
  | 'updateMarketsBatch'
  | 'setActiveMarketGroupId'
  | 'setDiscoveryParams'
  | 'setDiscoveryLoading'
  | 'setSubCategoriesLoading'
  | 'setDetailLoading'
  | 'setBrowseLoading'
  | 'setDiscoveryError'
  | 'setSubCategoriesError'
  | 'setDetailError'
  | 'setBrowseError'
  | 'reset'
>;

const initialState: NormalizedMarketsStateData = {
  marketGroupsById: new Map(),
  normalizedMarketGroupsById: new Map(),
  marketIdToGroupId: new Map(),
  marketTimestamps: new Map(),
  activeMarketGroupId: null,
  discoveryParams: null,
  discoverySections: [],
  subCategories: [],
  isDiscoveryLoading: false,
  isSubCategoriesLoading: false,
  isDetailLoading: false,
  isBrowseLoading: false,
  discoveryError: null,
  subCategoriesError: null,
  detailError: null,
  browseError: null,
};

export const useNormalizedMarketsStore = createWithEqualityFn<NormalizedMarketsState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setSubCategories: (subCategories) =>
        set({ subCategories, subCategoriesError: null }, false, 'setSubCategories'),

      setDiscoveryData: (sections, eventsById) => {
        const state = get();

        const allMarketGroups = sections.flatMap((s) => s.market_groups);

        const { rawMap, normalizedMap, marketIdToGroupIdMap } = mergeAndNormalizeMarketGroups(
          state.marketGroupsById,
          state.normalizedMarketGroupsById,
          state.marketIdToGroupId,
          allMarketGroups,
          eventsById
        );

        set(
          {
            discoverySections: sections,
            marketGroupsById: rawMap,
            normalizedMarketGroupsById: normalizedMap,
            marketIdToGroupId: marketIdToGroupIdMap,
            discoveryError: null,
          },
          false,
          'setDiscoveryData'
        );
      },

      appendBrowseData: (marketGroups, eventsById) => {
        const state = get();

        const { rawMap, normalizedMap, marketIdToGroupIdMap } = mergeAndNormalizeMarketGroups(
          state.marketGroupsById,
          state.normalizedMarketGroupsById,
          state.marketIdToGroupId,
          marketGroups,
          eventsById
        );

        set(
          {
            marketGroupsById: rawMap,
            normalizedMarketGroupsById: normalizedMap,
            marketIdToGroupId: marketIdToGroupIdMap,
            browseError: null,
          },
          false,
          'appendBrowseData'
        );
      },

      setDetailData: (marketGroup, eventsById) => {
        const state = get();

        const { rawMap, normalizedMap, marketIdToGroupIdMap } = mergeAndNormalizeMarketGroups(
          state.marketGroupsById,
          state.normalizedMarketGroupsById,
          state.marketIdToGroupId,
          [marketGroup],
          eventsById
        );

        set(
          {
            marketGroupsById: rawMap,
            normalizedMarketGroupsById: normalizedMap,
            marketIdToGroupId: marketIdToGroupIdMap,
            activeMarketGroupId: marketGroup.id,
            detailError: null,
          },
          false,
          'setDetailData'
        );
      },

      updateMarket: (marketId, marketData, timestamp) => {
        const state = get();

        if (timestamp !== undefined) {
          const existingTimestamp = state.marketTimestamps.get(marketId) ?? 0;
          if (timestamp <= existingTimestamp) {
            return;
          }
        }

        const groupId = state.marketIdToGroupId.get(marketId);
        if (!groupId) {
          return;
        }

        const marketGroup = state.marketGroupsById.get(groupId);
        if (!marketGroup) {
          return;
        }

        const updatedMarkets = marketGroup.markets.map((m) =>
          m.id === marketId ? { ...m, ...marketData } : m
        );
        const updatedAlternateMarkets = (marketGroup.alternate_markets ?? []).map((m) =>
          m.id === marketId ? { ...m, ...marketData } : m
        );

        const updatedMarketGroup: PublicMarketGroup = {
          ...marketGroup,
          markets: updatedMarkets,
          alternate_markets: updatedAlternateMarkets,
        };

        const newTimestamps =
          timestamp !== undefined
            ? new Map(state.marketTimestamps).set(marketId, timestamp)
            : state.marketTimestamps;

        const existingNormalized = state.normalizedMarketGroupsById.get(groupId);
        if (!existingNormalized) {
          const rawMap = new Map(state.marketGroupsById);
          rawMap.set(groupId, updatedMarketGroup);
          set({ marketGroupsById: rawMap, marketTimestamps: newTimestamps }, false, 'updateMarket');
          return;
        }

        const {
          homeTeamLine,
          awayTeamLine,
          spreadMarketId,
          totalMarketId,
          homeMoneylineMarketId,
          awayMoneylineMarketId,
        } = extractBettingLinesForGroup(
          updatedMarkets,
          existingNormalized.homeTeamId,
          existingNormalized.awayTeamId
        );

        const updatedNormalized: NormalizedMarketGroup = {
          ...existingNormalized,
          homeTeamLine,
          awayTeamLine,
          spreadMarketId,
          totalMarketId,
          homeMoneylineMarketId,
          awayMoneylineMarketId,
          markets: updatedMarkets,
          alternateMarkets: updatedAlternateMarkets,
        };

        const rawMap = new Map(state.marketGroupsById);
        rawMap.set(groupId, updatedMarketGroup);

        const normalizedMap = new Map(state.normalizedMarketGroupsById);
        normalizedMap.set(groupId, updatedNormalized);

        set(
          {
            marketGroupsById: rawMap,
            normalizedMarketGroupsById: normalizedMap,
            marketTimestamps: newTimestamps,
          },
          false,
          'updateMarket'
        );
      },

      updateMarketsBatch: (updates) => {
        if (updates.length === 0) return;

        const state = get();

        const rawMap = new Map(state.marketGroupsById);
        const normalizedMap = new Map(state.normalizedMarketGroupsById);
        const newTimestamps = new Map(state.marketTimestamps);

        for (const { marketId, market: marketData, timestamp } of updates) {
          let shouldProcess = true;
          if (timestamp !== undefined) {
            const existingTimestamp = newTimestamps.get(marketId) ?? 0;
            if (timestamp <= existingTimestamp) {
              shouldProcess = false;
            } else {
              newTimestamps.set(marketId, timestamp);
            }
          }

          const groupId = state.marketIdToGroupId.get(marketId);
          const marketGroup = groupId ? rawMap.get(groupId) : undefined;

          if (shouldProcess && groupId && marketGroup) {
            const updatedMarkets = marketGroup.markets.map((m) =>
              m.id === marketId ? { ...m, ...marketData } : m
            );
            const updatedAlternateMarkets = (marketGroup.alternate_markets ?? []).map((m) =>
              m.id === marketId ? { ...m, ...marketData } : m
            );

            const updatedMarketGroup: PublicMarketGroup = {
              ...marketGroup,
              markets: updatedMarkets,
              alternate_markets: updatedAlternateMarkets,
            };

            rawMap.set(groupId, updatedMarketGroup);

            const existingNormalized = normalizedMap.get(groupId);
            if (existingNormalized) {
              const {
                homeTeamLine,
                awayTeamLine,
                spreadMarketId,
                totalMarketId,
                homeMoneylineMarketId,
                awayMoneylineMarketId,
              } = extractBettingLinesForGroup(
                updatedMarkets,
                existingNormalized.homeTeamId,
                existingNormalized.awayTeamId
              );

              const updatedNormalized: NormalizedMarketGroup = {
                ...existingNormalized,
                homeTeamLine,
                awayTeamLine,
                spreadMarketId,
                totalMarketId,
                homeMoneylineMarketId,
                awayMoneylineMarketId,
                markets: updatedMarkets,
                alternateMarkets: updatedAlternateMarkets,
              };

              normalizedMap.set(groupId, updatedNormalized);
            }
          }
        }

        set(
          {
            marketGroupsById: rawMap,
            normalizedMarketGroupsById: normalizedMap,
            marketTimestamps: newTimestamps,
          },
          false,
          'updateMarketsBatch'
        );
      },

      setActiveMarketGroupId: (id) =>
        set({ activeMarketGroupId: id }, false, 'setActiveMarketGroupId'),

      setDiscoveryParams: (params) => set({ discoveryParams: params }, false, 'setDiscoveryParams'),

      setDiscoveryLoading: (loading) =>
        set({ isDiscoveryLoading: loading }, false, 'setDiscoveryLoading'),

      setSubCategoriesLoading: (loading) =>
        set({ isSubCategoriesLoading: loading }, false, 'setSubCategoriesLoading'),

      setDetailLoading: (loading) => set({ isDetailLoading: loading }, false, 'setDetailLoading'),

      setBrowseLoading: (loading) => set({ isBrowseLoading: loading }, false, 'setBrowseLoading'),

      setDiscoveryError: (error) =>
        set({ discoveryError: error, isDiscoveryLoading: false }, false, 'setDiscoveryError'),

      setSubCategoriesError: (error) =>
        set(
          { subCategoriesError: error, isSubCategoriesLoading: false },
          false,
          'setSubCategoriesError'
        ),

      setDetailError: (error) =>
        set({ detailError: error, isDetailLoading: false }, false, 'setDetailError'),

      setBrowseError: (error) =>
        set({ browseError: error, isBrowseLoading: false }, false, 'setBrowseError'),

      reset: () => set(initialState, false, 'reset'),
    }),
    { name: 'NormalizedMarketsStore' }
  ),
  shallow
);

export function useNormalizedMarketGroup(
  marketGroupId: string | null | undefined
): NormalizedMarketGroup | undefined {
  return useNormalizedMarketsStore((state) =>
    marketGroupId ? state.normalizedMarketGroupsById.get(marketGroupId) : undefined
  );
}

export function useMarketGroup(
  marketGroupId: string | null | undefined
): PublicMarketGroup | undefined {
  return useNormalizedMarketsStore((state) =>
    marketGroupId ? state.marketGroupsById.get(marketGroupId) : undefined
  );
}

export function useActiveMarketGroup(): NormalizedMarketGroup | undefined {
  return useNormalizedMarketsStore((state) =>
    state.activeMarketGroupId
      ? state.normalizedMarketGroupsById.get(state.activeMarketGroupId)
      : undefined
  );
}

export function useDiscoverySections(): PublicMarketGroupGamelineSection[] {
  return useNormalizedMarketsStore((state) => state.discoverySections);
}

export function useSubCategories(): SubCategory[] {
  return useNormalizedMarketsStore((state) => state.subCategories);
}

export function useIsDiscoveryLoading(): boolean {
  return useNormalizedMarketsStore((state) => state.isDiscoveryLoading);
}

export function useIsDetailLoading(): boolean {
  return useNormalizedMarketsStore((state) => state.isDetailLoading);
}

export function useAllNormalizedMarketGroups(): NormalizedMarketGroup[] {
  return useNormalizedMarketsStore((state) =>
    Array.from(state.normalizedMarketGroupsById.values())
  );
}
